import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgxSpinnerModule, NgxSpinnerService} from "ngx-spinner";
import {TranslatePipe} from "@ngx-translate/core";
import {AssetSyncService, GeotabAsset, PaginatedAssetsResponse} from '../../services/asset-sync.service';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../../environments/environment';
import {createInfiniteScrollObserver} from '../../shared/infinite-scroll.util';

@Component({
  selector: 'app-asset-sync',
  imports: [
      NgxSpinnerModule,
      TranslatePipe
  ],
  templateUrl: './asset-sync.component.html',
  styleUrl: './asset-sync.component.scss',
  standalone: true
})
export class AssetSyncComponent implements OnInit, AfterViewInit, OnDestroy {
  assets: GeotabAsset[] = [];
  nextStartFromKey: string | null = null;
  activeInfoVin: string | null = null;
  host: string = environment.host;
  spinnerTemplate = `<div style='display: flex; justify-content: center; align-items: center; height: 100%; width: 100%;'><img src='${this.host}/assets/loader/loader.gif' alt='Loading' style='width: 100px; height: auto;' /></div>`;
  private readonly pageSize = 100;
  private isLoadingAssets = false;
  private intersectionObserver?: IntersectionObserver;

  @ViewChild('scrollSentinel') private readonly scrollSentinel?: ElementRef<HTMLDivElement>;

  constructor(public assetSyncService: AssetSyncService,
              private readonly spinnerService: NgxSpinnerService,
              private readonly cdRef: ChangeDetectorRef){
  }

  async ngOnInit(){
    await this.loadAssets(true);
  }

  ngAfterViewInit(): void {
    this.attachScrollListener();
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  async loadNextPage(): Promise<void> {
    if (!this.nextStartFromKey) {
      return;
    }
    await this.loadAssets(false);
  }

  getActivationStatusKey(asset: GeotabAsset): string {
    return asset.geotabActivationStatus === 'ACTIVE' && asset.ridecareActivationStatus === 'ACTIVE'
      ? 'ASSET_SYNC.activated'
      : 'ASSET_SYNC.deactivated';
  }

  shouldShowMissingSubscriptionInfo(asset: GeotabAsset): boolean {
    return this.getMissingSubscriptionInfoKeys(asset).length > 0;
  }

  getMissingSubscriptionInfoKeys(asset: GeotabAsset): string[] {
    if (asset?.ridecareActivationStatus === asset?.geotabActivationStatus) {
      return [];
    }

    const missingInfoKeys: string[] = [];
    if (asset?.ridecareActivationStatus !== 'ACTIVE') {
      missingInfoKeys.push('ASSET_SYNC.ridecareSubscriptionMissing');
    }
    if (asset?.geotabActivationStatus !== 'ACTIVE') {
      missingInfoKeys.push('ASSET_SYNC.geotabSubscriptionMissing');
    }

    return missingInfoKeys;
  }

  toggleInfoTooltip(vin?: string): void {
    if (!vin) {
      this.activeInfoVin = null;
      return;
    }

    this.activeInfoVin = this.activeInfoVin === vin ? null : vin;
  }

  private async loadAssets(isInitialLoad: boolean): Promise<void> {
    if (this.isLoadingAssets) {
      return;
    }

    this.isLoadingAssets = true;

    try {
      if (isInitialLoad) {
        this.spinnerService.show();
      }

      const response = await firstValueFrom(
        this.assetSyncService.getAssetSync(isInitialLoad ? undefined : this.nextStartFromKey ?? undefined, this.pageSize)
      );
      this.mergeAssets(response, isInitialLoad);
      this.cdRef.detectChanges();
    }
    catch(error){
      console.error('Error fetching asset sync data:', error);
    }
    finally {
      this.isLoadingAssets = false;
      if (isInitialLoad) {
        this.spinnerService.hide();
      }
    }
  }

  private mergeAssets(response: PaginatedAssetsResponse | undefined, isInitialLoad: boolean): void {
    const nextAssets = response?.assets ?? [];
    this.assets = isInitialLoad ? nextAssets : [...this.assets, ...nextAssets];
    this.nextStartFromKey = response?.nextStartFromKey ?? null;
  }

  private attachScrollListener(): void {
    this.intersectionObserver = createInfiniteScrollObserver({
      target: this.scrollSentinel?.nativeElement,
      previousObserver: this.intersectionObserver,
      onIntersect: () => {
        if (!this.isLoadingAssets && this.nextStartFromKey) {
          void this.loadNextPage();
        }
      },
    });
  }
}

